<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useCompaniesStore } from 'dashboard/stores/companies';
import { useDealsStore } from 'dashboard/stores/deals';
import { DuplicateCompanyException } from 'shared/helpers/CustomErrors';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import CompaniesForm from 'dashboard/components-next/Companies/CompaniesForm/CompaniesForm.vue';
import CompanyDeleteSection from 'dashboard/components-next/Companies/CompanyDetail/CompanyDeleteSection.vue';
import CompanyContactsSidebar from 'dashboard/components-next/Companies/CompanyDetail/CompanyContactsSidebar.vue';
import CompanyHistorySidebar from 'dashboard/components-next/Companies/CompanyDetail/CompanyHistorySidebar.vue';
import DealInfo from 'dashboard/routes/dashboard/conversation/contact/DealInfo.vue';
import LinkedTasks from 'dashboard/components-next/Tasks/LinkedTasks.vue';

const props = defineProps({
  company: { type: Object, required: true },
});

const emit = defineEmits(['back', 'updated']);

const { t } = useI18n();
const companiesStore = useCompaniesStore();
const dealsStore = useDealsStore();

const companiesFormRef = ref(null);
const isFormInvalid = computed(() => companiesFormRef.value?.isFormInvalid);
const isUpdating = computed(() => companiesStore.uiFlags.updatingItem);

const companyData = computed(() => ({
  id: props.company.id,
  name: props.company.name,
  domain: props.company.domain,
  description: props.company.description,
  email: props.company.email,
  phoneNumber: props.company.phoneNumber,
  city: props.company.city,
  countryCode: props.company.countryCode,
  socialProfiles: props.company.socialProfiles,
  cnpj: props.company.cnpj,
  customAttributes: props.company.customAttributes,
}));

const formData = ref({ ...companyData.value });
const handleFormUpdate = updated => {
  formData.value = { ...updated };
};

const handleUpdateCompany = async () => {
  const data = companiesFormRef.value?.state || formData.value;
  try {
    await companiesStore.update({
      id: props.company.id,
      name: data.name?.trim(),
      domain: data.domain?.trim() || null,
      description: data.description?.trim() || null,
      email: data.email?.trim() || null,
      phoneNumber: data.phoneNumber?.trim() || null,
      city: data.city?.trim() || null,
      countryCode: data.countryCode || null,
      socialProfiles: data.socialProfiles || {},
      cnpj: data.cnpj?.trim() || null,
      customAttributes: data.customAttributes || {},
    });
    useAlert(t('COMPANIES.DETAIL.PROFILE.MESSAGES.UPDATE_SUCCESS'));
    emit('updated');
  } catch (error) {
    if (error instanceof DuplicateCompanyException) {
      useAlert(t('COMPANIES.DETAIL.PROFILE.MESSAGES.CNPJ_DUPLICATE'));
    } else {
      useAlert(t('COMPANIES.DETAIL.PROFILE.MESSAGES.UPDATE_ERROR'));
    }
  }
};

// --- Tabs: Detalhes / Contatos / Negociações / Tarefas / Conversas ---
const TABS_OPTIONS = [
  { key: 'DETAILS', value: 'details' },
  { key: 'CONTACTS', value: 'contacts' },
  { key: 'DEALS', value: 'deals' },
  { key: 'TASKS', value: 'tasks' },
  { key: 'CONVERSATIONS', value: 'conversations' },
];

const activeTab = ref('details');
const dealsCount = ref(0);
const tasksCount = ref(0);
// --- Conversas (conversas do inbox vinculadas aos contatos da empresa) ---
const companyConversations = computed(
  () => companiesStore.companyConversations || []
);

const fetchDealsCount = async () => {
  try {
    const deals = await dealsStore.getByCompany(props.company.id);
    dealsCount.value = deals.length;
  } catch {
    dealsCount.value = 0;
  }
};

const tabs = computed(() =>
  TABS_OPTIONS.map(tab => {
    if (tab.value === 'contacts') {
      return {
        label: t('COMPANIES.DETAIL.TABS.CONTACTS'),
        count: props.company.contactsCount || 0,
        value: tab.value,
      };
    }
    if (tab.value === 'deals') {
      return {
        label: t('COMPANIES.DETAIL.TABS.DEALS'),
        count: dealsCount.value > 0 ? dealsCount.value : undefined,
        value: tab.value,
      };
    }
    if (tab.value === 'tasks') {
      return {
        label: t('TASKS.HEADER'),
        count: tasksCount.value > 0 ? tasksCount.value : undefined,
        value: tab.value,
      };
    }
    if (tab.value === 'conversations') {
      return {
        label: t('COMPANIES.DETAIL.TABS.CONVERSATIONS'),
        count:
          companyConversations.value.length > 0
            ? companyConversations.value.length
            : undefined,
        value: tab.value,
      };
    }
    return { label: t(`COMPANIES.DETAIL.TABS.${tab.key}`), value: tab.value };
  })
);

const activeTabIndex = computed(() =>
  TABS_OPTIONS.findIndex(tab => tab.value === activeTab.value)
);

// --- Contatos vinculados (busca/adicionar/remover) ---
const selectedContactCandidate = ref(null);
const companyContacts = computed(() => companiesStore.companyContacts);
const companyContactsMeta = computed(() => companiesStore.companyContactsMeta);
const contactSearchResults = computed(
  () => companiesStore.contactSearchResults
);
const uiFlags = computed(() => companiesStore.uiFlags);
const isFetchingContacts = computed(() => uiFlags.value.fetchingContacts);
const isSearchingContacts = computed(() => uiFlags.value.searchingContacts);
const isManagingContacts = computed(
  () => uiFlags.value.creatingContact || uiFlags.value.removingContact
);

const clearSelectedContactCandidate = () => {
  selectedContactCandidate.value = null;
};

const loadContactsTab = () => {
  companiesStore.getCompanyContacts(props.company.id, 1);
  companiesStore.searchCompanyContactCandidates({
    companyId: props.company.id,
    search: '',
  });
};

const handleContactSearch = async query => {
  await companiesStore.searchCompanyContactCandidates({
    companyId: props.company.id,
    search: query,
  });
};

const handleConfirmContactSelection = async () => {
  const candidate = selectedContactCandidate.value;
  if (!candidate) return;

  const isReassigning =
    candidate.company?.id && candidate.company.id !== props.company.id;
  const message = isReassigning
    ? t('COMPANIES.DETAIL.CONTACTS.MESSAGES.REASSIGN_SUCCESS')
    : t('COMPANIES.DETAIL.CONTACTS.MESSAGES.ADD_SUCCESS');

  try {
    await companiesStore.attachContactToCompany(props.company.id, candidate.id);
    useAlert(message);
    clearSelectedContactCandidate();
    companiesStore.searchCompanyContactCandidates({
      companyId: props.company.id,
      search: '',
    });
  } catch {
    const errorMessage = isReassigning
      ? t('COMPANIES.DETAIL.CONTACTS.MESSAGES.REASSIGN_ERROR')
      : t('COMPANIES.DETAIL.CONTACTS.MESSAGES.ADD_ERROR');
    useAlert(errorMessage);
  }
};

const handleRemoveContact = async contactId => {
  const currentPage = Number(companyContactsMeta.value.page || 1);
  const nextPage =
    currentPage > 1 && companyContacts.value.length === 1
      ? currentPage - 1
      : currentPage;

  try {
    await companiesStore.removeContactFromCompany(
      props.company.id,
      contactId,
      nextPage
    );
    useAlert(t('COMPANIES.DETAIL.CONTACTS.MESSAGES.REMOVE_SUCCESS'));
    companiesStore.searchCompanyContactCandidates({
      companyId: props.company.id,
      search: '',
    });
  } catch {
    useAlert(t('COMPANIES.DETAIL.CONTACTS.MESSAGES.REMOVE_ERROR'));
  }
};

const loadCompanyContactsPage = async page => {
  await companiesStore.getCompanyContacts(props.company.id, page);
};

const isFetchingConversations = computed(
  () => uiFlags.value.fetchingConversations
);

const handleTabChange = tab => {
  activeTab.value = tab.value;
  if (tab.value === 'contacts') loadContactsTab();
  if (tab.value === 'conversations') {
    companiesStore.getCompanyConversations(props.company.id);
  }
};

watch(
  () => props.company.id,
  () => {
    activeTab.value = 'details';
    fetchDealsCount();
    companiesStore.getCompanyConversations(props.company.id);
  }
);

onMounted(() => {
  fetchDealsCount();
  companiesStore.getCompanyConversations(props.company.id);
});
</script>

<template>
  <div
    class="thin-scrollbar flex flex-col gap-4 w-full h-full overflow-y-auto border-r border-n-weak bg-n-solid-1 p-4"
  >
    <div class="flex items-center gap-2">
      <Button
        icon="i-lucide-arrow-left"
        variant="ghost"
        color="slate"
        size="xs"
        @click="emit('back')"
      />
      <Avatar
        :name="company.name || t('COMPANIES.UNNAMED')"
        :src="company.avatarUrl"
        :size="40"
        rounded-full
      />
      <div class="flex flex-col min-w-0">
        <span
          class="flex items-center gap-1.5 text-sm font-medium text-n-slate-12"
        >
          <span class="truncate">{{
            company.name || t('COMPANIES.UNNAMED')
          }}</span>
          <span
            class="i-lucide-building size-4 shrink-0 text-n-slate-11"
            aria-hidden="true"
          />
        </span>
        <span v-if="company.domain" class="text-xs text-n-slate-11 truncate">
          {{ company.domain }}
        </span>
      </div>
    </div>

    <TabBar
      :tabs="tabs"
      :initial-active-tab="activeTabIndex"
      class="w-full [&>button]:w-full bg-n-alpha-black2"
      @tab-changed="handleTabChange"
    />

    <div v-if="activeTab === 'details'" class="flex flex-col gap-4">
      <CompaniesForm
        ref="companiesFormRef"
        :company-data="companyData"
        @update="handleFormUpdate"
      />
      <Button
        :label="t('COMPANIES.DETAIL.PROFILE.ACTIONS.SAVE')"
        size="sm"
        :is-loading="isUpdating"
        :disabled="isUpdating || isFormInvalid"
        @click="handleUpdateCompany"
      />
      <CompanyDeleteSection
        :selected-company="{ id: company.id, name: company.name }"
      />
    </div>
    <CompanyContactsSidebar
      v-if="activeTab === 'contacts'"
      :company="company"
      :contacts="companyContacts"
      :meta="companyContactsMeta"
      :is-loading="isFetchingContacts"
      :is-busy="isManagingContacts"
      :search-results="contactSearchResults"
      :is-searching="isSearchingContacts"
      :selected-contact="selectedContactCandidate"
      @cancel-contact-selection="clearSelectedContactCandidate"
      @confirm-contact-selection="handleConfirmContactSelection"
      @search="handleContactSearch"
      @select-contact="contact => (selectedContactCandidate = contact)"
      @remove-contact="handleRemoveContact"
      @update:current-page="loadCompanyContactsPage"
    />
    <DealInfo
      v-if="activeTab === 'deals'"
      highlight-rows
      :company-id="company.id"
      @deals-changed="count => (dealsCount = count)"
    />
    <LinkedTasks
      v-show="activeTab === 'tasks'"
      :company-id="company.id"
      @loaded="count => (tasksCount = count)"
    />
    <CompanyHistorySidebar
      v-if="activeTab === 'conversations'"
      :conversations="companyConversations"
      :is-loading="isFetchingConversations"
    />
  </div>
</template>
